import { useEffect, useRef, useState, type ReactNode } from 'react';

import {
  Box,
  ButtonBase,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import BottomSheet, { SheetSurface } from '@/designs-dev/device/BottomSheet';
import PushScreen from '@/designs-dev/device/PushScreen';
import StatusBar from '@/designs-dev/device/StatusBar';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import FactList from '@/karehero-app/tell-us-once/intake/FactList';
import ClaimRow from '@/karehero-app/tell-us-once/intake/ClaimRow';
import ResultBanner from '@/karehero-app/care/ResultBanner';
import CalculatorBar from '@/karehero-app/tell-us-once/benefits/CalculatorBar';
import { roundButton } from '@/karehero-app/shell/BackChevron';
import ChatV2 from '@/karehero-app/tell-us-once/intake/ChatV2';
import {
  IntakeStyleContext,
  ONBOARDING_2_0_STYLE,
} from '@/karehero-app/tell-us-once/intake/IntakeStyle';
import { AA_BAR } from '@/karehero-app/tell-us-once/AttendanceAllowance.content';
import {
  AA_ANSWERS,
  AA_ANSWERS_PAGE,
  AA_ANSWERS_FINISHED,
  AA_FINISHED,
  AA_NOT_ELIGIBLE,
  AA_REVIEW,
  AA_SUMMARY,
  AA_TO_CHECK,
  AA_CHAT,
  AA_PROGRESS,
  AA_STAGE_LINES,
  AA_STAGE_OF,
  AA_STAGES,
} from '@/karehero-app/care/AaClaim.content';

const noop = () => {};

/** How long a stage takes to fold open or shut. */
const FOLD_MS = 280;

/**
 * A stage's status in the brand's pale colours, solid, with Marine Green text:
 * lime once it's done, the same lime as the hub's icon circles, and sunflower
 * while it's being worked on. The theme's own Chip colours are the strong
 * shades, which went olive and loud at this size.
 */
const StageChip = ({ status }: { status: string }) => (
  <Chip
    size='small'
    label={status}
    sx={{
      bgcolor:
        status === AA_PROGRESS.done
          ? 'custom.limeGreen.200'
          : 'custom.sunflower.200',
      color: 'primary.main',
    }}
  />
);

/**
 * CONCEPT. The claim's stages as a list: done, the one they're on, and the
 * rest. In the claim's drawer on Money, and behind the bar's info button.
 */
export const AaStages = ({
  done,
  started = true,
  lines = false,
}: {
  done: number;
  /** Off before the claim is started, so nothing is in progress yet. */
  started?: boolean;
  /** A line under each stage on what it asks, for before starting. */
  lines?: boolean;
}) => (
  <Stack spacing={1.5}>
    {AA_STAGES.map((name, i) => {
      // Not started says nothing, so the chips are only where things are.
      const status =
        i < done
          ? AA_PROGRESS.done
          : started && i === done
            ? AA_PROGRESS.current
            : undefined;
      return (
        <Stack
          key={name}
          direction='row'
          spacing={1.5}
          sx={{ alignItems: 'center', minHeight: 24 }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant='body1'>{name}</Typography>
            {lines && (
              <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                {AA_STAGE_LINES[i]}
              </Typography>
            )}
          </Box>
          {status && <StageChip status={status} />}
        </Stack>
      );
    })}
  </Stack>
);

/**
 * CONCEPT. The claim's answers so far, from its drawer on Money: the stages as
 * the same list the benefits check's answers use, each folding to its name
 * and status, one open at a time. A stage nobody has started has nothing to
 * open. Carry on goes back into the chat.
 *
 * Once every stage is answered, the same page leads with what it found and a
 * call to finish the application with a care expert, and loses its chips and
 * Carry on. The answers can still be changed until then.
 */
export const AaAnswers = ({
  done,
  finished = false,
  eligible = true,
  bar,
  onClose,
  initialOpen = 0,
}: {
  done: number;
  /** Every stage is answered, ready for a care expert to go through. */
  finished?: boolean;
  /** With finished: the answers say no, so the result is quieter. */
  eligible?: boolean;
  /** The pushed page's bar, drawn by the hub so it matches its other pages. */
  bar: (title: string, close: () => void) => ReactNode;
  onClose: () => void;
  /** Harness-only: which stage opens first. */
  initialOpen?: number;
}) => {
  const [open, setOpen] = useState<number | undefined>(initialOpen);
  const outcome = eligible ? AA_FINISHED : AA_NOT_ELIGIBLE;
  return (
    <PushScreen onClose={onClose} bar={(close) => bar(AA_BAR.title, close)}>
      {(close) => (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2, pb: 4 }}>
          <Stack spacing={2}>
            {finished && (
              // What it found, then the way on: a care expert finishes it
              // with them before anything is sent.
              <Stack spacing={2} sx={{ pb: 1 }}>
                <ResultBanner
                  tone={eligible ? 'lime' : 'neutral'}
                  icon={eligible ? 'check' : 'circle-info'}
                  {...outcome.result}
                />
                <ClaimRow {...outcome.call} lead filled='cherry' />
              </Stack>
            )}
            {AA_STAGES.map((name, i) => {
              const status = finished
                ? undefined
                : i < done
                  ? AA_PROGRESS.done
                  : i === done
                    ? AA_PROGRESS.current
                    : undefined;
              const rows = finished
                ? AA_ANSWERS_FINISHED[i]
                : status && AA_ANSWERS[i];
              const opened = rows && open === i;
              return (
                <Stack key={name}>
                  <ButtonBase
                    disabled={!rows}
                    onClick={() => setOpen(opened ? undefined : i)}
                    sx={{ gap: 1.5, minHeight: 32, textAlign: 'left' }}
                  >
                    <Typography variant='h6' sx={{ flex: 1, minWidth: 0 }}>
                      {name}
                    </Typography>
                    {status && <StageChip status={status} />}
                    {/* Room kept where there's nothing to open, so the chips
                        line up. */}
                    {/* One chevron that turns, rather than two swapped. */}
                    <Box
                      sx={{
                        width: 16,
                        display: 'flex',
                        color: 'text.secondary',
                        transition: `transform ${FOLD_MS}ms ease`,
                        transform: opened ? 'rotate(180deg)' : 'none',
                      }}
                    >
                      {rows && (
                        <Icon icon={'chevron-down' as never} fontSize={14} />
                      )}
                    </Box>
                  </ButtonBase>
                  {/* Slides open and shut, the next stage moving with it. */}
                  {rows && (
                    <Collapse in={Boolean(opened)} timeout={FOLD_MS}>
                      <Box sx={{ pt: 1.5 }}>
                        <FactList groups={[{ label: '', rows }]} />
                      </Box>
                    </Collapse>
                  )}
                </Stack>
              );
            })}
            {!finished && (
              <Box sx={{ pt: 1 }}>
                <Button
                  variant='primary'
                  size='lg'
                  isFullWidth
                  ariaLabel={AA_ANSWERS_PAGE.carryOn}
                  onPress={close}
                >
                  {AA_ANSWERS_PAGE.carryOn}
                </Button>
              </Box>
            )}
          </Stack>
        </Box>
      )}
    </PushScreen>
  );
};

/**
 * The claim's bar: the calculator's, with the stage it's on under the title
 * and its progress as the bottom edge in place of the divider, five segments
 * edge to edge, never a count of questions. The info button opens the stages
 * in a sheet.
 */
const AaBar = ({
  stage,
  subtitle = AA_STAGES[stage],
  percent = false,
}: {
  stage: number;
  /** In place of the stage's name, for a page that isn't one. */
  subtitle?: string;
  /**
   * To trial: how far through as a percentage under the title, in place of
   * the stage's name and the segments, with the bar's usual line back.
   */
  percent?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <CalculatorBar
        content={AA_BAR}
        back
        divider={percent}
        subtitle={
          percent
            ? AA_PROGRESS.percent(
                Math.round((Math.max(stage, 0) / AA_STAGES.length) * 100),
              )
            : subtitle
        }
        action={
          <IconButton
            aria-label={AA_PROGRESS.sheet}
            onClick={() => setOpen(true)}
            sx={roundButton}
          >
            <Icon icon={'circle-info' as never} fontSize={16} />
          </IconButton>
        }
      />
      {!percent && (
        <Stack direction='row' spacing={0.25}>
          {AA_STAGES.map((name, i) => (
            <Box
              key={name}
              sx={{
                flex: 1,
                height: 4,
                bgcolor:
                  i < stage
                    ? 'primary.main'
                    : i === stage
                      ? 'custom.coral.500'
                      : 'custom.neutral.200',
              }}
            />
          ))}
        </Stack>
      )}
      {open && (
        <BottomSheet
          variant='hug'
          title={AA_PROGRESS.sheet}
          onClose={() => setOpen(false)}
        >
          <Stack spacing={2} sx={{ pb: 1 }}>
            <Typography variant='body1'>{AA_PROGRESS.intro}</Typography>
            <AaStages done={stage} />
          </Stack>
        </BottomSheet>
      )}
    </>
  );
};

/**
 * CONCEPT. The finished claim, reviewed a section at a time before the call.
 * First, everything that needs them: what's missing, and what the AI read but
 * wasn't sure of. Then each stage, its topics summed up. They swipe between
 * sections, and dots above the button say where they are. One button, Done
 * reviewing, on every page.
 */
export const AaReview = ({
  initialSection = 0,
  onDone,
}: {
  /** Harness-only: open on this section, 0 being what needs them. */
  initialSection?: number;
  /** Done reviewing: on to the claim's results page. */
  onDone?: () => void;
}) => {
  const track = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState(initialSection);
  const sections = [AA_REVIEW.first, ...AA_STAGES];

  // Harness-only: open swiped along to a later section, without animating.
  useEffect(() => {
    const el = track.current;
    if (el && initialSection) el.scrollLeft = el.clientWidth * initialSection;
  }, [initialSection]);

  const go = (i: number) =>
    track.current?.scrollTo({
      left: track.current.clientWidth * i,
      behavior: 'smooth',
    });

  return (
    <ScreenRoot>
      <StatusBar />
      <CalculatorBar content={AA_BAR} back subtitle={AA_REVIEW.subtitle} />
      <Box
        ref={track}
        onScroll={(e) => {
          const el = e.currentTarget;
          setAt(Math.round(el.scrollLeft / el.clientWidth));
        }}
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {sections.map((name, i) => (
          <Box
            key={name}
            sx={{
              width: '100%',
              flexShrink: 0,
              scrollSnapAlign: 'start',
              overflowY: 'auto',
              px: 2,
              pt: 1,
            }}
          >
            <Stack spacing={2}>
              <Typography variant='h4'>{name}</Typography>
              {i === 0 && (
                <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                  {AA_REVIEW.how}
                </Typography>
              )}
              <FactList
                groups={[
                  {
                    label: '',
                    rows: i === 0 ? AA_TO_CHECK.rows : AA_SUMMARY[i - 1],
                  },
                ]}
              />
            </Stack>
          </Box>
        ))}
      </Box>
      <Box sx={{ px: 2, pt: 2, pb: 4 }}>
        {/* Where they are: a dot per section, the current one filled. */}
        <Stack
          direction='row'
          spacing={1}
          sx={{ justifyContent: 'center', pb: 2 }}
        >
          {sections.map((name, i) => (
            <Box
              key={name}
              component='button'
              aria-label={name}
              aria-current={i === at}
              onClick={() => go(i)}
              sx={{
                width: 8,
                height: 8,
                p: 0,
                border: 0,
                borderRadius: '50%',
                cursor: 'pointer',
                bgcolor: i === at ? 'primary.main' : 'custom.neutral.300',
                transition: 'background-color 200ms',
              }}
            />
          ))}
        </Stack>
        <Button
          variant='primary'
          size='lg'
          isFullWidth
          ariaLabel={AA_REVIEW.done}
          onPress={() => onDone?.()}
        >
          {AA_REVIEW.done}
        </Button>
      </Box>
    </ScreenRoot>
  );
};

/**
 * CONCEPT. The Attendance Allowance claim, in the calculator's chat. The bar
 * follows the conversation's stage.
 */
const AaClaim = ({
  at = 'special-rules',
  questions = AA_CHAT,
  percent = false,
  extra,
}: {
  /** To trial: progress as a percentage under the title, no segments. */
  percent?: boolean;
  /** The conversation, for a variant spliced into the main one. */
  questions?: typeof AA_CHAT;
  /** Harness-only: open at this question, the ones before it answered. */
  at?: string;
  /** Harness-only: anything else the chat takes, like the plus's drawer. */
  extra?: Partial<Parameters<typeof ChatV2>[0]>;
}) => {
  const [stage, setStage] = useState(AA_STAGE_OF[at] ?? 0);
  const startAt = Math.max(
    0,
    questions.findIndex((q) => q.id === at),
  );

  return (
    <IntakeStyleContext.Provider value={ONBOARDING_2_0_STYLE}>
      {/* Floral White drawers, as in the rest of the hub. */}
      <SheetSurface.Provider value='paper'>
        <ChatV2
          questions={questions}
          startAt={startAt}
          onExit={noop}
          bar={<AaBar stage={stage} percent={percent} />}
          inlineMic
          liveVoice
          onQuestionChange={(id) => {
            setStage(AA_STAGE_OF[id] ?? stage);
          }}
          {...extra}
        />
      </SheetSurface.Provider>
    </IntakeStyleContext.Provider>
  );
};

export default AaClaim;
